import cx from 'classnames';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { IApplicationState } from '../../../../../types/redux';
import { IHotspotProperties, TFeature } from '../../../../../types/map';
import { useMapContext } from '../../../context';
import { useFeaturesContext } from '../context';
import * as styles from './DotHover.css';

interface IDotHoverProps {
  resultsAvailable: boolean;
  detailsVisible: boolean;
}

export const DotHover: React.FC<IDotHoverProps> = ({
  resultsAvailable: isResultsAvailable,
  detailsVisible: isDetailsVisible,
}) => {
  const { ymaps, map } = useMapContext();
  const { hotspotsLayer } = useFeaturesContext();
  const selectedFeature = useSelector<IApplicationState, TFeature | null>(state => state.activeFeature);
  const isNewbuildingsListing = useSelector<IApplicationState, boolean>(state => state.isNewbuildingsListing);
  const iconLayout = React.useRef((isNewbuildingsListing: boolean, isSmall: boolean) =>
    ymaps.templateLayoutFactory.createClass(
      `<div class="${cx(styles['container'], {
        [styles['container--small']]: isSmall,
        [styles['container--blue']]: !isNewbuildingsListing,
        [styles['container--green']]: isNewbuildingsListing,
      })}">{% if properties.count > 1 %}{% if properties.count <= 9 %}{{properties.count}}{% else %}9+{% endif %}{% endif %}</div>`,
    ),
  );
  const placemarkObject = React.useRef<YMaps.GeoObject | null>(null);

  const handleHotspotMouseEnter = React.useCallback(
    (event: YMaps.IEvent) => {
      if (!isResultsAvailable || isDetailsVisible) {
        return;
      }

      const activeObject = event.get<YMaps.IHotspotLayerObject<IHotspotProperties>>('activeObject');
      const originalFeature = activeObject.getProperties().originalFeature;

      if (selectedFeature && selectedFeature.id === originalFeature.id) {
        return;
      }

      const isSmallPlacemark = originalFeature.properties.count === 1;

      placemarkObject.current = new ymaps.GeoObject(
        {
          ...originalFeature,
          type: 'Feature',
          id: 'DotHoverFeature',
        },
        {
          iconLayout: iconLayout.current(isNewbuildingsListing, isSmallPlacemark),
        },
      );

      map.geoObjects.add(placemarkObject.current);
    },
    [isDetailsVisible, isNewbuildingsListing, isResultsAvailable, map, selectedFeature, ymaps],
  );

  const handleHotspotMouseLeave = React.useCallback(() => {
    if (placemarkObject.current) {
      map.geoObjects.remove(placemarkObject.current);
      placemarkObject.current = null;
    }
  }, [map]);

  React.useEffect(() => {
    hotspotsLayer.layer.events.add('mouseenter', handleHotspotMouseEnter);
    hotspotsLayer.layer.events.add('mouseleave', handleHotspotMouseLeave);

    return () => {
      hotspotsLayer.layer.events.remove('mouseenter', handleHotspotMouseEnter);
      hotspotsLayer.layer.events.remove('mouseleave', handleHotspotMouseLeave);
    };
  }, [handleHotspotMouseEnter, handleHotspotMouseLeave, hotspotsLayer]);

  React.useEffect(() => {
    if (selectedFeature && placemarkObject.current) {
      map.geoObjects.remove(placemarkObject.current);
      placemarkObject.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeature]);

  return null;
};
